import { Link, useLocation } from '@tanstack/react-router'
import { machineHref } from '@/lib/machine-view'

// HUMAN / MACHINE (OpenSpec 137): the page you read, and the markdown an agent reads. Plain links
// (full page loads): /machine/* is served by the worker, not by the router.
function ViewSwitch() {
  const { pathname } = useLocation()
  const item = 'px-3 py-1 transition-colors hover:text-foreground'
  return (
    <div role="group" aria-label="View" className="inline-flex w-fit overflow-hidden rounded-sm border border-border font-mono text-sm">
      <a href={pathname} aria-current="page" className={`${item} bg-muted text-foreground`}>Human</a>
      <a href={machineHref(pathname)} className={`${item} text-muted-foreground`}>Machine</a>
    </div>
  )
}

/** `machineView`: this page has a markdown version, so show the HUMAN / MACHINE switch. */
export function SiteFooter({ machineView = false }: { machineView?: boolean } = {}) {
  return (
    <footer className="border-t py-8">
      <div className="container mx-auto px-4">
        <div className="flex flex-col gap-6 sm:gap-8">
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-4">
            <div>
              <h2 className="mb-3 text-sm font-semibold">Features</h2>
              <nav aria-label="Features" className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/features/uptime-monitoring" className="transition-colors hover:text-foreground">Uptime Monitoring</Link>
                <Link to="/features/ssl-monitoring" className="transition-colors hover:text-foreground">SSL Monitoring</Link>
                <Link to="/features/dns-monitoring" className="transition-colors hover:text-foreground">DNS Monitoring</Link>
                <Link to="/features/heartbeat-monitoring" className="transition-colors hover:text-foreground">Cron & Heartbeat</Link>
                <Link to="/features/status-pages" className="transition-colors hover:text-foreground">Status Pages</Link>
                <Link to="/features/browser-monitoring" className="transition-colors hover:text-foreground">Browser Monitoring</Link>
                <Link to="/ai" className="transition-colors hover:text-foreground">AI &amp; MCP</Link>
                <Link to="/pricing" className="transition-colors hover:text-foreground">Pricing</Link>
                <Link to="/changelog" className="transition-colors hover:text-foreground">Changelog</Link>
                <Link to="/status/$slug" params={{ slug: 'uptrack' }} className="transition-colors hover:text-foreground">Uptrack Status</Link>
              </nav>
            </div>
            <div>
              <h2 className="mb-3 text-sm font-semibold">Integrations</h2>
              <nav aria-label="Integrations" className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/integrations/slack" className="transition-colors hover:text-foreground">Slack</Link>
                <Link to="/integrations/discord" className="transition-colors hover:text-foreground">Discord</Link>
                <Link to="/integrations/telegram" className="transition-colors hover:text-foreground">Telegram</Link>
                <Link to="/integrations/email" className="transition-colors hover:text-foreground">Email</Link>
                <Link to="/integrations/n8n" className="transition-colors hover:text-foreground">n8n</Link>
                <Link to="/guides/webhook-payloads" className="transition-colors hover:text-foreground">Webhooks</Link>
                <Link to="/guides" className="transition-colors hover:text-foreground">All Guides</Link>
              </nav>
            </div>
            <div>
              <h2 className="mb-3 text-sm font-semibold">Tools</h2>
              <nav aria-label="Tools" className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/free-uptime-monitoring" className="transition-colors hover:text-foreground">Free Uptime Monitoring</Link>
                <Link to="/tools/browser-check" className="transition-colors hover:text-foreground">Free Browser Check</Link>
                <Link to="/tools/is-website-down" className="transition-colors hover:text-foreground">Is Website Down?</Link>
                <Link to="/tools/is-website-down/$domain" params={{ domain: 'chatgpt.com' }} className="transition-colors hover:text-foreground">Is ChatGPT Down?</Link>
                <Link to="/tools/is-website-down/$domain" params={{ domain: 'github.com' }} className="transition-colors hover:text-foreground">Is GitHub Down?</Link>
                <Link to="/tools/is-website-down/$domain" params={{ domain: 'amazon.com' }} className="transition-colors hover:text-foreground">Is Amazon Down?</Link>
                <Link to="/tools/is-website-down/$domain" params={{ domain: 'twitch.tv' }} className="transition-colors hover:text-foreground">Is Twitch Down?</Link>
                <Link to="/tools/is-website-down/$domain" params={{ domain: 'peacocktv.com' }} className="transition-colors hover:text-foreground">Is Peacock Down?</Link>
                <Link to="/tools/is-website-down/$domain" params={{ domain: 'paramountplus.com' }} className="transition-colors hover:text-foreground">Is Paramount Plus Down?</Link>
                <Link to="/tools" className="transition-colors hover:text-foreground">All Free Tools</Link>
                <Link to="/chat" className="transition-colors hover:text-foreground">Ask Uptrack</Link>
                <Link to="/blog/22-check-locations" className="transition-colors hover:text-foreground">How Consensus Works</Link>
              </nav>
            </div>
            <div>
              <h2 className="mb-3 text-sm font-semibold">Compare</h2>
              <nav aria-label="Compare" className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/compare/uptimerobot" className="transition-colors hover:text-foreground">vs UptimeRobot</Link>
                <Link to="/compare/betterstack" className="transition-colors hover:text-foreground">vs Better Stack</Link>
                <Link to="/compare/pingdom" className="transition-colors hover:text-foreground">vs Pingdom</Link>
                <Link to="/compare/datadog" className="transition-colors hover:text-foreground">vs Datadog</Link>
                <Link to="/compare/hetrixtools" className="transition-colors hover:text-foreground">vs HetrixTools</Link>
                <Link to="/compare/uptime-kuma" className="transition-colors hover:text-foreground">vs Uptime Kuma</Link>
                <Link to="/compare" className="transition-colors hover:text-foreground">All comparisons</Link>
                <Link to="/alternatives" className="transition-colors hover:text-foreground">Alternatives</Link>
                <Link to="/blog" className="transition-colors hover:text-foreground">Blog</Link>
              </nav>
            </div>
            <div>
              <h2 className="mb-3 text-sm font-semibold">Learn</h2>
              <nav aria-label="Learn" className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/glossary" className="transition-colors hover:text-foreground">Glossary</Link>
                <Link to="/http" className="transition-colors hover:text-foreground">HTTP Status Codes</Link>
                <Link to="/cron-monitoring" className="transition-colors hover:text-foreground">Cron Job Monitoring</Link>
                <Link to="/docs/check-locations" className="transition-colors hover:text-foreground">Check Locations</Link>
              </nav>
            </div>
            <div>
              <h2 className="mb-3 text-sm font-semibold">Legal</h2>
              <nav aria-label="Legal" className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/privacy" className="transition-colors hover:text-foreground">Privacy</Link>
                <Link to="/terms" className="transition-colors hover:text-foreground">Terms</Link>
                <Link to="/refund" className="transition-colors hover:text-foreground">Refund Policy</Link>
              </nav>
            </div>
            <div>
              <h2 className="mb-3 text-sm font-semibold">Follow</h2>
              <nav aria-label="Follow" className="flex flex-col gap-2 text-sm text-muted-foreground">
                <a href="https://x.com/UptrackDotApp" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-foreground">X</a>
                <a href="https://www.linkedin.com/company/uptrack-app" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-foreground">LinkedIn</a>
              </nav>
            </div>
          </div>
          <div className="border-t pt-6">
            <div className="flex flex-col gap-1 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <p>&copy; {new Date().getFullYear()} Uptrack. All rights reserved.</p>
              <p>Made with care for people who ship</p>
            </div>
            {machineView && (
              <div className="mt-4">
                <ViewSwitch />
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  )
}
